import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { buildImagePath } from '../../JobComponents/Company/Image/logo';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';

import './EmbedlyPreview.scss';

const injectedTransforms = {
    embedly: 'f_auto,q_auto,c_scale,w_450',
    close: 'f_auto,q_auto,c_scale',
};

const closeBtnSrc = buildImagePath(
    '/v1/SVGs/circle_x.svg',
    'close',
    injectedTransforms
);

// eslint-disable-next-line no-control-regex
const URL_REGEX = new RegExp('(?:https?://)?(?:[^@/\n]+@)?(?:www.)?([^:/\n]+)');

const EmbedlyPreview = ({ url, send }) => {
    const [embedlyData, setEmbedlyData] = useState(null);

    const imgSrc =
        embedlyData?.previewImage &&
        buildImagePath(embedlyData.previewImage, 'embedly', injectedTransforms);

    useEffect(() => {
        (async () => {
            try {
                const result = await axios.post(
                    '/api/community/community-links/preview',
                    { url }
                );
                const resultData = result && result.data && result.data.data;

                const { cloudinaryId, title, description, hash } = resultData;
                setEmbedlyData({
                    previewImage: cloudinaryId,
                    previewTitle: title,
                    previewDescription: description,
                    hash,
                });
                if (!cloudinaryId) {
                    EmitMetric({
                        misc_event_type: 'link-preview-success-no-image',
                        misc_event_count: 1,
                        link_url: url,
                    });
                } else {
                    EmitMetric({
                        misc_event_type: 'link-preview-success',
                        misc_event_count: 1,
                        link_url: url,
                    });
                }
                send('SAVE_EMBEDLY', { url, hash });
            } catch (error) {
                EmitMetric({
                    misc_event_type: 'link-preview-failure',
                    misc_event_count: 1,
                    link_url: url,
                });
            }
        })();
    }, [url, send]);

    const handlePreviewClose = useCallback(() => {
        send('DISABLE_PREVIEW');
        send('SET_LINK_TO_DEFAULT');
        EmitMetric({
            misc_event_type: 'link-preview-user-close',
            misc_event_count: 1,
            link_url: url,
        });
    }, [url, send]);

    const strippedUrl = useMemo(() => {
        if (!url) return '';

        return url.match(URL_REGEX)[1];
    }, [url]);

    return (
        <div className="component-UnifiedPostBox-EmbedlyPreview">
            <div className="contentContainer">
                {embedlyData && (
                    <button
                        className="close-button"
                        onClick={handlePreviewClose}
                        type="submit"
                    >
                        <img src={closeBtnSrc} alt="clear-preview-icon" />
                    </button>
                )}
                {embedlyData?.previewImage && (
                    <img
                        src={imgSrc}
                        alt={`preview-${embedlyData.previewImage}`}
                        className="preview-image"
                    />
                )}
                {embedlyData && (
                    <p className="embedly-preview-title">{strippedUrl}</p>
                )}
                {embedlyData?.previewTitle && (
                    <p className="embedly-preview-description">
                        {embedlyData.previewTitle}
                    </p>
                )}
            </div>
        </div>
    );
};

EmbedlyPreview.propTypes = {
    url: PropTypes.string.isRequired,
    send: PropTypes.func,
};

EmbedlyPreview.defaultProps = {
    send: () => {},
};

EmbedlyPreview.displayName = 'EmbedlyPreview';

export default EmbedlyPreview;
